import React, { memo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid, Box, Typography, MenuItem, ListItemText, Stack, Checkbox } from '@mui/material';
import { CustomSelect } from './style';
import { setInformationTypes } from 'app/store/features/complianceSlice';
import { getDeviationsPerInfoType } from 'app/store/features/complianceSlice';
import { ColorsV2 } from 'app/constants/color';
import { camelToSpaceCase } from 'app/utils';
import ViolenceBarChart from './ViolenceBarChart';

const InformationClassSection = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const dispatch = useDispatch();
  const {
    data: { dataClasses: informationDataClasses },
  } = useSelector(state => state.compliance.deviationsPerInformationTypes);

  const prevSelectedInformationTypes = useSelector(
    state => state.compliance.selectedInformationTypes
  );
  const allPolicies = useSelector(state => state.privacy.allPolicies);
  const [selectedInformationTypes, setSelectedInformationTypes] = useState(
    prevSelectedInformationTypes
  );

  const { t } = useTranslation('translation', { keyPrefix: 'compliance' });

  const handleDropdownChange = event => {
    const { value } = event.target;
    const filteredValue = value.filter(val => val.id !== 'all');
    const dataLength = informationDataClasses.length;
    if (
      filteredValue.some(val => val.id !== 'all') &&
      selectedInformationTypes.length === dataLength
    ) {
      const filteredData = selectedInformationTypes.filter(
        item => filteredValue[filteredValue.length - 1].id !== item.id
      );
      setSelectedInformationTypes(filteredData);
    } else {
      const filteredData = filteredValue.filter(
        val => val.id === filteredValue[filteredValue.length - 1].id
      );
      if (filteredData.length === 2)
        setSelectedInformationTypes(
          filteredValue.filter(val => val.id !== filteredValue[filteredValue.length - 1].id)
        );
      else setSelectedInformationTypes(filteredValue);
    }
  };

  const handleClose = () => {
    if (allPolicies?.id) {
      const dataSourceIds = selectedInformationTypes.some(item => item.id === 'all')
        ? informationDataClasses.map(item => item.id)
        : selectedInformationTypes.map(item => item.id);

      dispatch(setInformationTypes(selectedInformationTypes));

      prevSelectedInformationTypes !== selectedInformationTypes &&
        dispatch(
          getDeviationsPerInfoType({
            policyTypeId: allPolicies.policyType,
            payload: {
              classLevels: dataSourceIds,
            },
          })
        );
    }
    setMenuOpen(false);
  };

  return (
    <>
      <Grid
        item
        xs={5.9}
        overflow={'hidden'}
      >
        <Box
          backgroundColor={ColorsV2.primary}
          width={'100%'}
          display={'flex'}
          justifyContent={'space-between'}
          alignItems={'center'}
          padding={'0.45em'}
        >
          <Box>
            <Typography variant='subtitle1' sx={{color: ColorsV2.white}}>
              {t('violations_per_information_class')}
            </Typography>
          </Box>
          <Box
            sx={{
              marginInline: '0.75em',
            }}
          >
            <Stack
              sx={{
                width: '100%',
                justifyContent: 'flex-end',
                alignItems: 'center',
                height: 'auto',
              }}
              direction='row'
            >
              <Typography
                sx={{
                  marginRight: '0.25em',
                  color: 'white',
                  fontWeight: 'light',
                  fontSize: '0.7em',
                }}
              >
                {t('information_class')}
              </Typography>
              <CustomSelect
                name='information-class'
                onChange={handleDropdownChange}
                multiple
                open={menuOpen}
                onClose={handleClose}
                onOpen={() => setMenuOpen(true)}
                value={
                  selectedInformationTypes.length
                    ? selectedInformationTypes
                    : [{ name: 'All', id: 'all' }]
                }
                renderValue={selected => {
                  return !menuOpen
                    ? selectedInformationTypes.length === informationDataClasses.length
                      ? 'All'
                      : selected.map(value => value.name).join(', ')
                    : prevSelectedInformationTypes.length === informationDataClasses.length
                    ? 'All'
                    : prevSelectedInformationTypes.map(item => item.name).join(',');
                }}
                sx={{
                  width: 'max-content',
                  maxWidth: '200px',
                }}
                inputProps={{
                  'aria-label': 'Language Selector',
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        backgroundColor: ColorsV2.secondary,
                        color: ColorsV2.white,
                        border: `1px solid ${ColorsV2.outline} `,
                      },
                    },
                  },
                }}
              >
                {informationDataClasses?.map(item => {
                  return (
                    <MenuItem key={item.id} value={item}>
                      <Checkbox
                        style={{
                          color: ColorsV2.acent,
                        }}
                        checked={
                          selectedInformationTypes.length === informationDataClasses.length
                            ? true
                            : selectedInformationTypes.some(val => val.id === item.id)
                        }
                        size='small'
                        sx={{
                          '& .MuiSvgIcon-root': {
                            width: '0.8em',
                            height: '0.8em',
                          },
                        }}
                      />
                      <ListItemText
                        primary={item.name}
                        sx={{
                          '.MuiListItemText-primary': {
                            fontSize: '0.7em',
                          },
                        }}
                      />
                    </MenuItem>
                  );
                })}
              </CustomSelect>
            </Stack>
          </Box>
        </Box>

        <Grid item xs={12}>
          <RenderChart />
        </Grid>
      </Grid>
    </>
  );
};

const RenderChart = () => {
  const {
    data: { informationTypes },
    loading: informationTypesLoading,
  } = useSelector(state => state.compliance.deviationsPerInformationTypes);

  const informationTypesData = Object.entries(informationTypes || {}).map(([key, item]) => {
    return { 
      ...item || {}, 
      id: key, 
      name: camelToSpaceCase(item.name)
    };
  });
  return (
    <ViolenceBarChart unsortedChartData={informationTypesData} loading={informationTypesLoading} />
  );
};

export default memo(InformationClassSection);
